import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import {
  Mail,
  Lock,
  Building2,
  KeyRound,
  Eye,
  EyeOff,
  Loader2,
  FlaskConical,
  ShieldCheck,
} from "lucide-react";
import { RootState, AppDispatch } from "@/store/store";
import { login, clearError, getCurrentUser } from "@/store/slices/authSlice";
import { LoginCredentials, AdminRegisterCredentials, AccountType } from "@/types/auth";
import { authService } from "@/services/auth";
import queryClient from "@/queryClient";
import { ensureCsrfToken } from "@/utils/csrf";
import { useMockAuth } from "@/mocks/mockData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ForgotPasswordDialog from "@/components/auth/ForgotPasswordDialog";
import DevLoginAccountList from "@/components/auth/DevLoginAccountList";
import { DevLoginAccount, devLoginAccounts } from "@/lib/devLoginAccounts";
import { handleApiError, PASSWORD_REQUIREMENTS_HINT } from "@/services/api";
import { cn } from "@/lib/utils";
import { AxiosError } from "axios";

const MIN_PASSWORD_LENGTH = 12;

type TabValue = "dev" | "signin" | "register";

// DEV keeps the check statically false in builds, so the Dev Login tab is dropped too.
const hasDevLoginAccounts = import.meta.env.DEV && devLoginAccounts.length > 0;
// Three tabs do not fit equal grid columns at 390px; let the long label take its width,
// and tighten them on phones so all three stay inside the card.
const TAB_TRIGGER_CLASS = hasDevLoginAccounts
  ? "flex-1 px-1 text-xs sm:px-2 sm:text-sm"
  : undefined;

const ACCOUNT_TYPE_OPTIONS: {
  value: AccountType;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  {
    value: "standard",
    label: "Full account",
    description: "Inbox ingestion, your own contract templates and sender, team invites.",
    icon: ShieldCheck,
  },
  {
    value: "trial",
    label: "Trial account",
    description:
      "Manual uploads only. Settings stay at platform defaults and contracts/Plaid links are emailed to you instead of the merchant so you can test safely.",
    icon: FlaskConical,
  },
];

function FieldIcon({
  icon: Icon,
  children,
  className,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("relative", className)}>
      <Icon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      {children}
    </div>
  );
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, isAuthenticated, sessionChecked } = useSelector(
    (state: RootState) => state.auth
  );

  const [activeTab, setActiveTab] = useState<TabValue>(hasDevLoginAccounts ? "dev" : "signin");
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [accountType, setAccountType] = useState<AccountType>("standard");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [selectedDevEmail, setSelectedDevEmail] = useState<string | null>(null);
  const isTrialRegistration = accountType === "trial";

  // Back where sign-in was asked for, its query and fragment too: an approval link's ?choice=,
  // and a link's token can ride in the fragment (the agent's magic link).
  const returnTo = (
    location.state as {
      from?: { pathname?: string; search?: string; hash?: string };
    }
  )?.from;
  const from = returnTo?.pathname
    ? `${returnTo.pathname}${returnTo.search ?? ""}${returnTo.hash ?? ""}`
    : "/dashboard";

  useEffect(() => {
    if (useMockAuth) return;
    ensureCsrfToken().catch(() => undefined);
    if (!sessionChecked) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, sessionChecked]);

  useEffect(() => {
    if (sessionChecked && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [sessionChecked, isAuthenticated, navigate, from]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginCredentials>();

  const {
    register: registerField,
    handleSubmit: handleRegisterSubmit,
    formState: { errors: registerErrors },
    reset: resetRegisterForm,
  } = useForm<AdminRegisterCredentials>();

  const onSubmit = async (data: LoginCredentials) => {
    dispatch(clearError());
    const result = await dispatch(login(data));

    if (login.fulfilled.match(result)) {
      queryClient.clear();
      navigate(from, { replace: true });
    }
  };

  const onDevAccountSelect = ({ email, password }: DevLoginAccount) => {
    setSelectedDevEmail(email);
    void onSubmit({ email, password });
  };

  const onRegisterSubmit = async (data: AdminRegisterCredentials) => {
    setRegisterError(null);
    setRegisterLoading(true);
    try {
      data.email = data.email.trim().toLowerCase();
      await authService.registerAdmin({ ...data, account_type: accountType });
      setRegisterSuccess(true);
      resetRegisterForm();
      setTimeout(() => {
        setRegisterSuccess(false);
        setActiveTab("signin");
      }, 2500);
    } catch (err: unknown) {
      setRegisterError(
        err instanceof AxiosError
          ? handleApiError(err)
          : "Registration failed. Please check your details and try again."
      );
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabValue);
    setRegisterError(null);
    setRegisterSuccess(false);
    dispatch(clearError());
  };

  return (
    <div>
      <h2 className="mb-3 text-xl font-semibold">
        {activeTab !== "register"
          ? "Sign In"
          : isTrialRegistration
            ? "Create Trial Account"
            : "Create Admin Account"}
      </h2>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-2">
        <TabsList className={cn("w-full", hasDevLoginAccounts ? "flex" : "grid grid-cols-2")}>
          {hasDevLoginAccounts && (
            <TabsTrigger value="dev" className={TAB_TRIGGER_CLASS}>
              Dev Login
            </TabsTrigger>
          )}
          <TabsTrigger value="signin" className={TAB_TRIGGER_CLASS}>
            Sign In
          </TabsTrigger>
          <TabsTrigger value="register" className={TAB_TRIGGER_CLASS}>
            Create Account
          </TabsTrigger>
        </TabsList>

        {hasDevLoginAccounts && (
          <TabsContent value="dev" className="mt-6 space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <DevLoginAccountList
              accounts={devLoginAccounts}
              disabled={loading}
              pendingEmail={loading ? selectedDevEmail : null}
              onSelect={onDevAccountSelect}
            />
          </TabsContent>
        )}

        <TabsContent value="signin" className="mt-6">
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="signin-email">Email Address</Label>
              <FieldIcon icon={Mail}>
                <Input
                  id="signin-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  className="pl-10"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  aria-invalid={!!errors.email}
                />
              </FieldIcon>
              {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="signin-password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="signin-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className="pl-10 pr-10"
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters",
                    },
                  })}
                  aria-invalid={!!errors.password}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
              <div className="text-right">
                <button
                  type="button"
                  className="text-sm text-muted-foreground underline hover:text-foreground"
                  onClick={() => setForgotOpen(true)}
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full py-6 text-base font-semibold"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="register" className="mt-6">
          <form onSubmit={handleRegisterSubmit(onRegisterSubmit)} noValidate className="space-y-4">
            {registerError && (
              <Alert variant="destructive">
                <AlertDescription>{registerError}</AlertDescription>
              </Alert>
            )}

            {registerSuccess && (
              <Alert className="border-green-600/50 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100">
                <AlertDescription>
                  {isTrialRegistration
                    ? "Trial account created successfully! Redirecting to sign in..."
                    : "Admin account created successfully! Redirecting to sign in..."}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="reg-email">Email Address</Label>
              <FieldIcon icon={Mail}>
                <Input
                  id="reg-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  className="pl-10"
                  {...registerField("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  aria-invalid={!!registerErrors.email}
                />
              </FieldIcon>
              {registerErrors.email && (
                <p className="text-sm text-destructive">{registerErrors.email.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="reg-first-name">First name</Label>
                <Input
                  id="reg-first-name"
                  autoComplete="given-name"
                  {...registerField("first_name", {
                    required: "First name is required",
                    validate: (value) =>
                      (value || "").trim().length > 0 || "First name is required",
                  })}
                  aria-invalid={!!registerErrors.first_name}
                />
                {registerErrors.first_name && (
                  <p className="text-sm text-destructive">{registerErrors.first_name.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-last-name">Last name</Label>
                <Input
                  id="reg-last-name"
                  autoComplete="family-name"
                  {...registerField("last_name", {
                    required: "Last name is required",
                    validate: (value) => (value || "").trim().length > 0 || "Last name is required",
                  })}
                  aria-invalid={!!registerErrors.last_name}
                />
                {registerErrors.last_name && (
                  <p className="text-sm text-destructive">{registerErrors.last_name.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reg-company">Company Name</Label>
              <FieldIcon icon={Building2}>
                <Input
                  id="reg-company"
                  autoComplete="organization"
                  className="pl-10"
                  {...registerField("company_name", {
                    required: "Company name is required",
                  })}
                  aria-invalid={!!registerErrors.company_name}
                />
              </FieldIcon>
              {registerErrors.company_name && (
                <p className="text-sm text-destructive">{registerErrors.company_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label id="reg-account-type-label">Account Type</Label>
              <div
                role="radiogroup"
                aria-labelledby="reg-account-type-label"
                className="grid gap-2 sm:grid-cols-2"
              >
                {ACCOUNT_TYPE_OPTIONS.map((option) => {
                  const selected = accountType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      data-testid={`account-type-${option.value}`}
                      onClick={() => setAccountType(option.value)}
                      className={cn(
                        "flex flex-col gap-1 rounded-md border p-3 text-left text-sm transition-colors",
                        selected
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-input hover:bg-muted/50"
                      )}
                    >
                      <span className="inline-flex items-center gap-1.5 font-medium">
                        <option.icon className="h-4 w-4 text-muted-foreground" />
                        {option.label}
                      </span>
                      <span className="text-xs leading-snug text-muted-foreground">
                        {option.description}
                      </span>
                    </button>
                  );
                })}
              </div>
              {isTrialRegistration && (
                <p className="text-xs text-muted-foreground">
                  The same invite code is required. Contact support to convert a trial into a full
                  account later.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="reg-password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="reg-password"
                  type={showRegisterPassword ? "text" : "password"}
                  autoComplete="new-password"
                  className="pl-10 pr-10"
                  {...registerField("password", {
                    required: "Password is required",
                    minLength: {
                      value: MIN_PASSWORD_LENGTH,
                      message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
                    },
                  })}
                  aria-invalid={!!registerErrors.password}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                  aria-label="Toggle password visibility"
                >
                  {showRegisterPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {registerErrors.password && (
                <p className="text-sm text-destructive">{registerErrors.password.message}</p>
              )}
              <p className="text-xs text-muted-foreground">{PASSWORD_REQUIREMENTS_HINT}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reg-confirm">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="reg-confirm"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  className="pl-10 pr-10"
                  {...registerField("confirm_password", {
                    required: "Please confirm your password",
                  })}
                  aria-invalid={!!registerErrors.confirm_password}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label="Toggle confirm password visibility"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {registerErrors.confirm_password && (
                <p className="text-sm text-destructive">
                  {registerErrors.confirm_password.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="reg-invite">Invite Code</Label>
              <FieldIcon icon={KeyRound}>
                <Input
                  id="reg-invite"
                  type="password"
                  autoComplete="off"
                  className="pl-10"
                  {...registerField("invite_code", {
                    required: "Invite code is required",
                  })}
                  aria-invalid={!!registerErrors.invite_code}
                />
              </FieldIcon>
              {registerErrors.invite_code && (
                <p className="text-sm text-destructive">{registerErrors.invite_code.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full py-6 text-base font-semibold"
              disabled={registerLoading || registerSuccess}
            >
              {registerLoading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : isTrialRegistration ? (
                "Create Trial Account"
              ) : (
                "Create Admin Account"
              )}
            </Button>
          </form>
        </TabsContent>
      </Tabs>

      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        initialEmail={watch("email") || ""}
        onResetComplete={() => setActiveTab("signin")}
      />
    </div>
  );
};

export default Login;
